import React, { useState, useEffect, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import SearchButton from 'components/form/SearchButton'
import ContactSupportTooltip from 'components/ui/ContactSupportTooltip'
import { removeNonNumericCharacters } from 'utils/numbers'

const DEFAULT_ROW_CUT_WIDTH = 1380
const TAB_INDEX_START = [1, 10, 19, 28, 37, 46, 55, 64]

interface Props {
  course: Course
  version: CourseVersion | null
  syncHoles: (holes: CourseHole[]) => void
  isModifiable: boolean
  isCourseEditorUser?: boolean
  syncDuplicateHolesStatus?: (hasDuplicates: boolean) => void
}

export default function CourseHoles({
  course,
  version,
  syncHoles,
  isModifiable,
  isCourseEditorUser = false,
  syncDuplicateHolesStatus,
}: Props): JSX.Element {
  const [courseHoles, setCourseHoles] = useState<CourseHole[]>([])
  const [previousHoles, setPreviousHoles] = useState<CourseHole[]>([])
  const [duplicateHoleNumbers, setDuplicateHoleNumbers] = useState<number[]>([])
  const [duplicateHcpMen, setDuplicateHcpMen] = useState<number[]>([])
  const [duplicateHcpWomen, setDuplicateHcpWomen] = useState<number[]>([])
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth)
  const intl = useIntl()

  const duplicates: CourseHoleDuplicates = useMemo(() => {
    return {
      holeNumbers: duplicateHoleNumbers,
      hcpMen: duplicateHcpMen,
      hcpWomen: duplicateHcpWomen,
    }
  }, [duplicateHoleNumbers, duplicateHcpMen, duplicateHcpWomen])

  const validateHoles = useCallback(() => {
    const holeNumbers: number[] = courseHoles.map((ch: CourseHole) => ch.holeNumber)
    const hcpMen: number[] = courseHoles.map((ch: CourseHole) => ch.hcpMen)
    const hcpWomen: number[] = courseHoles.map((ch: CourseHole) => ch.hcpWomen)
    setDuplicateHoleNumbers([])
    setDuplicateHcpMen([])
    setDuplicateHcpWomen([])

    const duplicateHoles = holeNumbers.filter((value, index, array) => array.indexOf(value) !== index)
    if (duplicateHoles.length > 0) {
      setDuplicateHoleNumbers(duplicateHoles)
    }
    const duplicatesHcpMen = hcpMen.filter((value, index, array) => array.indexOf(value) !== index)
    if (duplicatesHcpMen.length > 0) {
      setDuplicateHcpMen(duplicatesHcpMen)
    }
    const duplicatesHcpWomen = hcpWomen.filter((value, index, array) => array.indexOf(value) !== index)
    if (duplicatesHcpWomen.length > 0) {
      setDuplicateHcpWomen(duplicatesHcpWomen)
    }
  }, [courseHoles])

  const courseHasOfficialVersion = useMemo(() => {
    const versions = course?.versions || []
    return versions.some((cv: CourseVersion) => cv.isOfficial)
  }, [course])

  useEffect(() => {
    function handleResize(event: Event) {
      const target = event.target as Window
      setWindowWidth(target.innerWidth)
    }
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    validateHoles()
  }, [courseHoles, validateHoles])

  useEffect(() => {
    if (syncDuplicateHolesStatus) {
      syncDuplicateHolesStatus(
        duplicates.holeNumbers.length > 0 || duplicates.hcpMen.length > 0 || duplicates.hcpWomen.length > 0,
      )
    }
  }, [duplicates, syncDuplicateHolesStatus])

  useEffect(() => {
    if (version && version.holes && (courseHoles.length === 0 || courseHoles[0].version !== version.version)) {
      const sortedHoles = version.holes.slice().sort((a, b) => (a.holeNumber > b.holeNumber ? 1 : -1))
      setCourseHoles(sortedHoles)
    }
  }, [version, courseHoles])

  useEffect(() => {
    if (!version && courseHoles.length === 0) {
      const holes: CourseHole[] = Array.from({ length: 18 }, (x, i) => {
        return { id: 0, holeNumber: i + 1, par: 0, version: 0, hcpMen: 0, hcpWomen: 0 }
      })
      setCourseHoles(holes)
    }
  }, [version, courseHoles])

  useEffect(() => {
    if (courseHoles !== previousHoles) {
      syncHoles(courseHoles)
      setPreviousHoles(courseHoles)
    }
  }, [courseHoles, syncHoles, previousHoles])

  const hcpAddedCount = useMemo(() => {
    let hcpMenCount = 0
    let hcpWomenCount = 0
    courseHoles.forEach((ch: CourseHole) => {
      if (ch.hcpMen) {
        hcpMenCount++
      }
      if (ch.hcpWomen) {
        hcpWomenCount++
      }
    })
    return { men: hcpMenCount, women: hcpWomenCount }
  }, [courseHoles])

  const getFront9Holes = () => {
    return courseHoles ? courseHoles.slice().splice(0, 9) : []
  }

  const getBack9Holes = () => {
    return courseHoles ? courseHoles.slice().splice(9, 9) : []
  }

  const modifyHoles = (e: React.ChangeEvent<HTMLInputElement>, field: string, index: number) => {
    const value = e.target.value ? Number(removeNonNumericCharacters(e.target.value)) : null
    const invalidValue = value !== null && (value < 1 || value > 18)
    if (invalidValue) {
      return
    }
    updateCourseHole(field, index, value)
  }

  const updateCourseHole = (field: string, index: number, value: number | null) => {
    setCourseHoles(
      [...courseHoles].map((ch: CourseHole, i: number) => {
        if (index !== i) {
          return ch
        }
        return {
          ...ch,
          [field]: value,
        }
      }),
    )
  }

  const getTotalOut = () => {
    return getFront9Holes().reduce((sum: number, ch: CourseHole) => sum + ch.par, 0)
  }

  const getTotalIn = () => {
    return getBack9Holes().reduce((sum: number, ch: CourseHole) => sum + ch.par, 0)
  }

  const getValue = (value: number) => {
    return value > 0 ? value : ''
  }

  const isDisabled = () => {
    return !isModifiable && version !== null
  }

  const insertDefaultHcpValues = () => {
    const defaultValues = [1, 3, 5, 7, 9, 11, 13, 15, 17, 2, 4, 6, 8, 10, 12, 14, 16, 18]
    const newHoles = courseHoles.map((ch: CourseHole, i: number) => {
      const newHole = { ...ch }
      newHole.hcpMen = defaultValues[i]
      newHole.hcpWomen = defaultValues[i]
      return newHole
    })
    setCourseHoles(newHoles)
  }

  const copyMenHcpToWomen = () => {
    const newHoles = courseHoles.map((ch: CourseHole) => {
      const newHole = { ...ch }
      newHole.hcpWomen = ch.hcpMen
      return newHole
    })
    setCourseHoles(newHoles)
  }

  const isDefaultHcpButtonDisabled = () => {
    return hcpAddedCount.men === 18 || hcpAddedCount.women === 18
  }

  const isCopyMenHcpToWomenDisabled = () => {
    return hcpAddedCount.men < 18
  }

  const renderDataView = () => {
    const isSideBySide = isCourseEditorUser && windowWidth > DEFAULT_ROW_CUT_WIDTH
    return (
      <DataContainer>
        <DataFront9 className={!isCourseEditorUser ? 'fullwidth' : ''}>
          <DataHeader className={isSideBySide ? 'no-right-radius' : ''}>
            <Front9Title>{intl.formatMessage({ id: 'strings.front9' })}</Front9Title>
          </DataHeader>
          {renderHoleRow('f9', 0)}
          {renderParRow('f9', isSideBySide ? 2 : 1)}
          {renderHcpMenRow('f9', isSideBySide ? 4 : 2)}
          {renderHcpWomenRow('f9', isSideBySide ? 6 : 3)}
        </DataFront9>
        <DataBack9 className={!isCourseEditorUser ? 'fullwidth' : ''}>
          <DataHeader className={isSideBySide ? 'no-left-radius' : ''}>
            <Back9Title className={!isSideBySide ? 'padding' : ''}>
              {intl.formatMessage({ id: 'strings.back9' })}
            </Back9Title>
          </DataHeader>
          {renderHoleRow('b9', isSideBySide ? 1 : 4)}
          {renderParRow('b9', isSideBySide ? 3 : 5)}
          {renderHcpMenRow('b9', isSideBySide ? 5 : 6)}
          {renderHcpWomenRow('b9', 7)}
        </DataBack9>
      </DataContainer>
    )
  }

  const renderFront9Inputs = ({
    valueKey,
    min,
    max,
    isDisabled,
    duplicateCheck,
    tabIndexStart,
    tooltipKey,
  }: CourseHoleDataInputs) => {
    return (
      <DataInputs>
        {getFront9Holes().map((ch: CourseHole, i: number) => (
          <ContactSupportTooltip
            key={i}
            title={tooltipKey ? intl.formatMessage({ id: tooltipKey }) : ''}
            position="left"
            onlyChildren={!tooltipKey || !isCourseEditorUser}
          >
            <HoleDataInput
              type="text"
              key={i}
              value={getValue(ch[valueKey])}
              onChange={(e) => modifyHoles(e, valueKey, i)}
              min={min}
              max={max}
              className={duplicateCheck?.includes(ch[valueKey]) ? 'duplicate' : ''}
              disabled={isDisabled}
              tabIndex={tabIndexStart + i}
            />
          </ContactSupportTooltip>
        ))}
      </DataInputs>
    )
  }

  const renderBack9Inputs = ({
    valueKey,
    min,
    max,
    isDisabled,
    duplicateCheck,
    tabIndexStart,
    tooltipKey,
  }: CourseHoleDataInputs) => {
    return (
      <DataInputs>
        {getBack9Holes().map((ch: CourseHole, i: number) => (
          <ContactSupportTooltip
            key={i}
            title={tooltipKey ? intl.formatMessage({ id: tooltipKey }) : ''}
            position="left"
            onlyChildren={!tooltipKey || !isCourseEditorUser}
          >
            <HoleDataInput
              type="text"
              key={i}
              value={getValue(ch[valueKey])}
              onChange={(e) => modifyHoles(e, valueKey, i + 9)}
              min={min}
              max={max}
              className={duplicateCheck?.includes(ch[valueKey]) ? 'duplicate' : ''}
              disabled={isDisabled}
              tabIndex={tabIndexStart + i}
            />
          </ContactSupportTooltip>
        ))}
      </DataInputs>
    )
  }

  const renderHoleRow = (holes: 'f9' | 'b9', order: number) => {
    const alwaysShowTitle = holes === 'f9' || !isCourseEditorUser
    return (
      <DataRow>
        <DataTitle className={alwaysShowTitle ? '' : 'toggle'}>{intl.formatMessage({ id: 'strings.hole' })}</DataTitle>
        {holes === 'f9' && (
          <>
            {renderFront9Inputs({
              valueKey: 'holeNumber',
              min: 1,
              max: 18,
              isDisabled: isDisabled() || courseHasOfficialVersion,
              duplicateCheck: duplicates?.holeNumbers,
              tabIndexStart: TAB_INDEX_START[order],
              tooltipKey: 'courseEditor.holeDataTooltip',
            })}
            <DataHalfwayText className="upper">{intl.formatMessage({ id: 'strings.out' })}</DataHalfwayText>
          </>
        )}
        {holes === 'b9' && (
          <>
            {renderBack9Inputs({
              valueKey: 'holeNumber',
              min: 1,
              max: 18,
              isDisabled: isDisabled() || courseHasOfficialVersion,
              duplicateCheck: duplicates?.holeNumbers,
              tabIndexStart: TAB_INDEX_START[order],
              tooltipKey: 'courseEditor.holeDataTooltip',
            })}
            <DataResults>
              <DataText className="upper">{intl.formatMessage({ id: 'strings.in' })}</DataText>
              <DataText className="upper">{intl.formatMessage({ id: 'strings.total' })}</DataText>
            </DataResults>
          </>
        )}
      </DataRow>
    )
  }

  const renderParRow = (holes: 'f9' | 'b9', order: number) => {
    const alwaysShowTitle = holes === 'f9' || !isCourseEditorUser
    return (
      <DataRow>
        <DataTitle className={`${alwaysShowTitle ? '' : 'toggle'} upper`}>
          {intl.formatMessage({ id: 'strings.par' })}
        </DataTitle>
        {holes === 'f9' && (
          <>
            {renderFront9Inputs({
              valueKey: 'par',
              min: 1,
              max: 9,
              isDisabled: isDisabled() || courseHasOfficialVersion,
              tabIndexStart: TAB_INDEX_START[order],
              tooltipKey: 'courseEditor.parDataTooltip',
            })}
            <DataHalfwayText>{getTotalOut()}</DataHalfwayText>
          </>
        )}
        {holes === 'b9' && (
          <>
            {renderBack9Inputs({
              valueKey: 'par',
              min: 1,
              max: 9,
              isDisabled: isDisabled() || courseHasOfficialVersion,
              tabIndexStart: TAB_INDEX_START[order],
              tooltipKey: 'courseEditor.parDataTooltip',
            })}
            <DataResults>
              <DataText>{getTotalIn()}</DataText>
              <DataText>{getTotalOut() + getTotalIn()}</DataText>
            </DataResults>
          </>
        )}
      </DataRow>
    )
  }

  const renderHcpMenRow = (holes: 'f9' | 'b9', order: number) => {
    const alwaysShowTitle = holes === 'f9' || !isCourseEditorUser
    return (
      <DataRow>
        <DataTitle className={alwaysShowTitle ? '' : 'toggle'}>
          {intl.formatMessage({ id: 'strings.hcp' })} / {intl.formatMessage({ id: 'strings.men' })}
        </DataTitle>

        {holes === 'f9' && (
          <>
            {renderFront9Inputs({
              valueKey: 'hcpMen',
              min: 1,
              max: 18,
              isDisabled: isDisabled(),
              duplicateCheck: duplicates?.hcpMen,
              tabIndexStart: TAB_INDEX_START[order],
            })}
            <DataHalfwayText></DataHalfwayText>
          </>
        )}
        {holes === 'b9' && (
          <>
            {renderBack9Inputs({
              valueKey: 'hcpMen',
              min: 1,
              max: 18,
              isDisabled: isDisabled(),
              duplicateCheck: duplicates?.hcpMen,
              tabIndexStart: TAB_INDEX_START[order],
            })}
          </>
        )}
      </DataRow>
    )
  }

  const renderHcpWomenRow = (holes: 'f9' | 'b9', order: number) => {
    const alwaysShowTitle = holes === 'f9' || !isCourseEditorUser
    return (
      <DataRow>
        <DataTitle className={alwaysShowTitle ? '' : 'toggle'}>
          {intl.formatMessage({ id: 'strings.hcp' })} / {intl.formatMessage({ id: 'strings.ladies' })}
        </DataTitle>
        {holes === 'f9' && (
          <>
            {renderFront9Inputs({
              valueKey: 'hcpWomen',
              min: 1,
              max: 18,
              isDisabled: isDisabled(),
              duplicateCheck: duplicates?.hcpWomen,
              tabIndexStart: TAB_INDEX_START[order],
            })}
            <DataHalfwayText></DataHalfwayText>
          </>
        )}
        {holes === 'b9' && (
          <>
            {renderBack9Inputs({
              valueKey: 'hcpWomen',
              min: 1,
              max: 18,
              isDisabled: isDisabled(),
              duplicateCheck: duplicates?.hcpWomen,
              tabIndexStart: TAB_INDEX_START[order],
            })}
          </>
        )}
      </DataRow>
    )
  }

  return (
    <CourseHolesContainer className={!isCourseEditorUser ? 'padding' : ''}>
      {!isCourseEditorUser && <SectionTitle>{intl.formatMessage({ id: 'strings.holeData' })}</SectionTitle>}
      {renderDataView()}
      {!isCourseEditorUser && (
        <InsertDefaultHcp
          disabled={isDefaultHcpButtonDisabled()}
          onClick={insertDefaultHcpValues}
          text={intl.formatMessage({ id: 'strings.insertDefaultHcpValues' })}
          status="idle"
        />
      )}
      <CopyMensHcpToLadies
        disabled={isCopyMenHcpToWomenDisabled()}
        onClick={copyMenHcpToWomen}
        text={intl.formatMessage({ id: 'strings.copyMensHcpValuesToLadies' })}
        status="idle"
      />
    </CourseHolesContainer>
  )
}

const CourseHolesContainer = styled.div`
  &.padding {
    padding: 0 20px;
  }
`

const SectionTitle = styled.h2`
  width: 100%;
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  color: ${(props) => props.theme.customPalette.darkGray2};
  margin-bottom: 25px;
`

const HoleDataInput = styled.input`
  width: 30px;
  height: 30px;
  text-align: center;
  border: 2px solid;
  border-color: #bbbbbd;
  border-radius: 5px;
  font-size: 16px;

  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &.duplicate {
    border: 2px solid;
    border-color: ${(props) => props.theme.customPalette.error};
  }

  &:disabled {
    color: #bbbbbd;
  }
`

const ExecuteAction = styled(SearchButton)`
  height: 53px;
  background-color: #fff;
  color: ${(props) => props.theme.palette.primary.main};
  border: 2px solid ${(props) => props.theme.palette.primary.main};
  border-radius: 8px;
  font-weight: 700;

  &:hover {
    background-color: #fff;
    opacity: 0.75;
  }
  &:disabled {
    border-color: transparent;
  }
`

const InsertDefaultHcp = styled(ExecuteAction)`
  margin-right: 20px;
`
const CopyMensHcpToLadies = styled(ExecuteAction)``

const DataContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  max-width: 1080px;
  margin: 30px 0 15px;
`

const Data9 = styled.div`
  width: 50%;

  & > div {
    &:nth-of-type(4),
    &:nth-of-type(5) {
      background-color: ${(props) => props.theme.customPalette.navActiveBg};
    }
  }

  &.fullwidth {
    width: 100%;
  }
  @media (max-width: ${DEFAULT_ROW_CUT_WIDTH}px) {
    width: 100%;
  }
`

const DataFront9 = styled(Data9)``
const DataBack9 = styled(Data9)``

const DataHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background-color: ${(props) => props.theme.palette.primary.dark};
  height: 41px;
  border-radius: 4px;
  margin-bottom: 20px;

  &.no-right-radius {
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
  }

  &.no-left-radius {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
  }
`

const HeaderTitle = styled.span`
  font-size: 13px;
  font-weight: 700;
  line-height: 15px;
  color: ${(props) => props.theme.customPalette.light};
  width: 50%;
  text-transform: uppercase;
`

const Front9Title = styled(HeaderTitle)`
  padding-left: 110px;
  width: calc(50% - 110px);
`

const Back9Title = styled(HeaderTitle)`
  &.padding {
    padding-left: 110px;
  }
`

const DataRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
  padding: 10px 0;
`

const DataTitle = styled.span`
  color: ${(props) => props.theme.customPalette.darkGray2};
  width: 100px;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  text-align: right;

  &.upper {
    text-transform: uppercase;
  }
  &.toggle {
    display: none;
    @media (max-width: ${DEFAULT_ROW_CUT_WIDTH}px) {
      display: inline;
    }
  }
`

const DataText = styled.span`
  color: ${(props) => props.theme.customPalette.darkGray2};
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  width: 50px;
  text-align: center;

  &.upper {
    text-transform: uppercase;
  }
`

const DataHalfwayText = styled(DataText)`
  width: 40px;
`

const DataResults = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  width: 100px;

  & > span:first-of-type {
    width: 20px;
  }
`

const DataInputs = styled.div`
  display: flex;
  gap: 5px;
`
